
import React from "react";
import { Capability, Schemes, Utils } from "../../../../utils";
import { i18nManager } from "../../../../i18n";
import { Elements } from "../../../shared/Elements";
import { Siren } from "../../../shared/ConfirmDialog";
import * as ReactRouterDom from "react-router-dom";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { BaseService, IGallery, IMember, IProfileWebsite, ITeam, ITeamWebsite, MemberService, SanitizedResponse, TeamService } from "../../../../services";
import { Alignment, Button, Checkbox, DOMHelper, Dropdown, Form, FormControl, NoseurObject, ObjectHelper, Scheme, TabPane, TabPanel, TextAreaInput, TextInput, useScreenDimension } from "@rivtn/noseur";
import ImportExport from "../../../page_design/ImportExport";
import Basic from "./general/Basic";
import SocialNetworks from "./general/SocialNetworks";
import TeamRules from "./general/TeamRules";
import BannerMedias from "./general/BannerMedias";
import WebsitePages from "./general/WebsitePages";
import Moderators from "./general/Moderators";
import TeamEarnings from "./general/TeamEarnings";
import Home from "./Home";
import Design from "./Design";
import Activities from "./Activities";
import Gallery from "../../../shared/settings/Gallery";
import Merch from "../../../shared/settings/Merch";
import News from "./News";
import Members from "./Members";

export interface TeamSettingsProps {
    profile: IMember;
}

function TeamSettings(props: TeamSettingsProps) {

    const { width } = useScreenDimension();
    const teamService = TeamService.getInstance();
    const [team, setTeam] = React.useState<ITeam>();
    const labels = i18nManager.Labels.teams.settings;
    let { id: teamId = "" } = ReactRouterDom.useParams();
    const merchRivtnAdStateState = React.useState<boolean>();
    const galleryRivtnAdStateState = React.useState<boolean>();
    const [profile, _] = React.useState<IMember>(props.profile);
    const teamWebsiteGalleryState = React.useState<IGallery[]>();
    const [teamWebsite, setTeamWebsite] = React.useState<ITeamWebsite>();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const tabs = ["GENERAL", "HOME", "MEMBERS", "NEWS", "MERCH", "ACTIVITIES", "GALLERY", "WEBSITE"];
    const genTabs = ["BASIC", "MODS", "TEARNS", "SNETS", "BMEDIAS", "WEBSITE_PAGES", "RULES", "IMEXPORT"];

    React.useEffect(fetchInformation, []);

    return (<div className="settings earnings analytics">
        <div className="header">
            <div className="l" style={{ display: "flex", flexDirection: "column" }}>
                <span className="t lined-limit"><i className="fa fa-arrow-left" onClick={() => window.history.back()} />
                    {labels.team} - {teamId}
                </span>
                <span className="lined-limit" style={{ marginLeft: 30, fontWeight: "normal" }}>
                    {team?.name}
                </span>
            </div>
        </div>
        {(!team || !teamWebsite)
            ? (<div style={{ margin: "auto" }}>
                <img alt={"loading"} src={LoadingGif} style={{ width: 80, margin: "auto" }} />
            </div>)
            : (<TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} activeIndex={tabs.indexOf(searchParams.get("tab") ?? "GENERAL")}
                style={{ marginTop: 10, marginLeft: 10, height: "100%" }} attrsRelay={{ content: { style: { margin: 0, padding: 0, border: "none" } } }} onTabActive={(i) => {
                    Utils.updateSearchParams("tab", tabs[i], setSearchParams);
                }}>
                <TabPanel header={labels.general.title} >
                    <div className="te-settings-gen">
                        <TabPane renderActiveTabOnly noUnderline idleScheme={Schemes.IDLE} scheme={Schemes.OMNEHOST} alignment={width > 800 ? Alignment.LEFT : Alignment.TOP}
                            activeIndex={genTabs.indexOf(searchParams.get("gtab") ?? "BASIC")} style={{ height: "100%", maxWidth: (width > 800 ? "100%" : "90vw") }}
                            attrsRelay={{ navigator: { headers: { style: { borderRight: "none", paddingLeft: (width > 800 ? 0 : 450) } } } }} onTabActive={(i) => {
                                Utils.updateSearchParams("gtab", genTabs[i], setSearchParams);
                            }}>
                            <TabPanel header={labels.general.basic.title} attrsRelay={{ header: { style: { marginTop: (width > 800 ? 30 : 20) } } }}>
                                <Basic team={team} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.moderators.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <Moderators team={team} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.team_earnings.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <TeamEarnings team={team} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.social_networks.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <SocialNetworks team={team} teamWebsite={teamWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.banner_medias.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <BannerMedias team={team} teamWebsite={teamWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.website_pages.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <WebsitePages team={team} teamWebsite={teamWebsite} profile={profile} />
                            </TabPanel>
                            <TabPanel header={labels.general.rules.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <TeamRules team={team} profile={profile} />
                            </TabPanel>
                            <TabPanel header={i18nManager.Labels.import_export.title} attrsRelay={{ header: { style: { marginTop: 20 } } }}>
                                <ImportExport copyCapability={Capability.DISABLE_TEAM_WEBSITE_SETTINGS_COPYING} importCapability={Capability.IMPORT_TEAM_WEBSITE_SETTINGS} exportCapability={Capability.EXPORT_TEAM_WEBSITE_SETTINGS} section="team_website" export={() => new Promise((resolve, _) => {
                                    setTimeout(() => {
                                        resolve(i18nManager.Labels.import_export.cannot_import_settings);
                                    }, 2000)
                                })} import={async (settings: NoseurObject<string>) => new Promise((resolve, _) => {
                                    setTimeout(() => {
                                        resolve(i18nManager.Labels.import_export.cannot_import_settings);
                                    }, 2000)
                                })} />
                            </TabPanel>
                        </TabPane>
                    </div>
                </TabPanel>

                <TabPanel header={labels.home.title} >
                    <Home team={team} teamWebsite={teamWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.members.title} >
                    <Members team={team} teamWebsite={teamWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.news.title} >
                    <News team={team} teamWebsite={teamWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.merch.title} >
                    <Merch noCompound profile={profile} enableRivtnAdState={merchRivtnAdStateState} updateSettings={(enableRivtnAd: boolean) => {
                        merchRivtnAdStateState[1](enableRivtnAd);
                        teamService.updateTeamWebsite(team.external_id, { ...teamWebsite, merchandise_settings: { ...teamWebsite.merchandise_settings, enable_rivtn_ad: enableRivtnAd } }).then(({ }: SanitizedResponse<ITeamWebsite>) => {
                            Siren.alert(labels.merch.update_successful);
                        }).catch(BaseService.reportError);
                    }} />
                </TabPanel>

                <TabPanel header={labels.activities.title} >
                    <Activities team={team} teamWebsite={teamWebsite} profile={profile} />
                </TabPanel>

                <TabPanel header={labels.gallery.title} >
                    <Gallery noCompound profile={profile} galleryState={teamWebsiteGalleryState}
                        currentCapabilityValue={teamWebsiteGalleryState[0]?.length ?? 0}
                        deleteLabel={i18nManager.Labels.account.profile_website.gallery.dialog.delete.title}
                        previewActionKey="action_label" previewLocationKey="action_location" previewCaptionKey="caption" uploadPath="/omnehost/team/gallery/"
                        updateGalleryMethod={(gallery) => teamService.updateGallery.bind(teamService)(team.external_id, gallery)} capability={Capability.MAX_TEAM_WEBSITE_GALLERY_UPLOAD}
                        enableRivtnAdState={galleryRivtnAdStateState} addGalleryMethod={(gallery) => teamService.createGallery.bind(teamService)(team.external_id, gallery)}
                        deleteGalleryMethod={(gallery) => teamService.deleteGallery.bind(teamService)(team.external_id, gallery)} enableAdLabel={i18nManager.Labels.account.profile_website.gallery.enable_ad}
                        createLabel={i18nManager.Labels.account.profile_website.gallery.dialog.add_update.add_media_to_gallery} updateLabel={i18nManager.Labels.account.profile_website.gallery.dialog.add_update.update_media_in_gallery}
                        formControls={[
                            {
                                type: "file",
                                name: "media_location",
                            },
                            [
                                {
                                    type: "textbox",
                                    name: "action_label",
                                },
                                {
                                    type: "textbox",
                                    name: "action_location",
                                }
                            ],
                            {
                                name: "caption",
                                type: "textarea",
                            },
                        ]}
                        onUnMount={() => {

                        }} fetchGallery={() => {
                            teamService.getGallery(team.external_id).then(({ apiResponse }: SanitizedResponse<IGallery>) => {
                                teamWebsiteGalleryState[1](apiResponse.data?.content);
                            }).catch(BaseService.reportError);
                        }} updateSettings={(enableRivtnAd: boolean) => {
                            galleryRivtnAdStateState[1](enableRivtnAd);
                            teamService.updateTeamWebsite(team.external_id, { ...teamWebsite, gallery_settings: { ...teamWebsite.gallery_settings, enable_rivtn_ad: enableRivtnAd } }).then(({ }: SanitizedResponse<ITeamWebsite>) => {
                                Siren.alert(labels.gallery.update_successful);
                            }).catch(BaseService.reportError);
                        }} />
                </TabPanel>

                <TabPanel header={labels.website.title} >
                    <Design team={team} teamWebsite={teamWebsite} profile={profile} />
                </TabPanel>
            </TabPane>)}
    </div>);

    function fetchInformation() {
        teamService.getTeamByPublicId(teamId!).then(({ sanitized }: SanitizedResponse<ITeam>) => {
            setTeam(sanitized);
            teamService.getTeamWebsite(sanitized.external_id).then(({ sanitized }: SanitizedResponse<ITeamWebsite>) => {
                setTeamWebsite(sanitized);
                galleryRivtnAdStateState[1](sanitized.gallery_settings.enable_rivtn_ad);
                merchRivtnAdStateState[1](sanitized.merchandise_settings.enable_rivtn_ad);
            }).catch(BaseService.reportError);
        }).catch(BaseService.reportError);
    }

}

export default TeamSettings;
